import { useEffect, useRef, useState } from "react";
import * as Icons from "lucide-react";
import { api, mediaUrl } from "../lib/api";
import { toast } from "sonner";

/**
 * Paired controls used on Reel Composer + Compile:
 *  - Branded outro toggle (uses listing realtor info)
 *  - Music track picker + uploader
 */
export default function OutroAndMusicControls({
  outroEnabled,
  onOutroChange,
  musicTrackId,
  onMusicChange,
}) {
  const [tracks, setTracks] = useState([]);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef();
  const audioRef = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get("/music");
      setTracks(data);
    } catch {}
  };
  useEffect(() => {
    load();
  }, []);

  const upload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      fd.append("name", f.name.replace(/\.[^.]+$/, ""));
      const { data } = await api.post("/music", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      await load();
      onMusicChange(data.id);
      toast.success("Track added");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const remove = async (id) => {
    if (!confirm("Delete this track from your library?")) return;
    await api.delete(`/music/${id}`);
    if (musicTrackId === id) onMusicChange(null);
    await load();
  };

  const preview = (id) => {
    if (audioRef.current) audioRef.current.pause();
    const a = new Audio(mediaUrl(`/api/public/music/${id}`));
    audioRef.current = a;
    a.play().catch(() => toast.error("Could not preview"));
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
      {/* Outro toggle */}
      <div className="bg-[#0A0A0A] border border-white/10 rounded-xl p-3">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <Icons.IdCard size={12} className="text-zinc-500" />
            <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-500">
              Branded Outro
            </div>
          </div>
          <button
            onClick={() => onOutroChange(!outroEnabled)}
            className={`relative w-10 h-5 rounded-full transition-all ${
              outroEnabled ? "bg-white" : "bg-white/10"
            }`}
            data-testid="outro-toggle"
          >
            <div
              className={`absolute top-0.5 w-4 h-4 rounded-full transition-all ${
                outroEnabled
                  ? "translate-x-5 bg-black"
                  : "translate-x-0.5 bg-white/60"
              }`}
            />
          </button>
        </div>
        <div className="text-[10px] text-zinc-600 leading-snug">
          Appends a 3-second card with the listing's realtor (name, brokerage,
          headshot, contact). Configure on the realtor in the CRM sidebar.
        </div>
      </div>

      {/* Music picker */}
      <div className="bg-[#0A0A0A] border border-white/10 rounded-xl p-3">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <Icons.Music size={12} className="text-zinc-500" />
            <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-500">
              Music
            </div>
          </div>
          <button
            onClick={() => fileRef.current?.click()}
            className="text-[10px] text-zinc-400 hover:text-white border border-white/10 rounded px-2 py-1 flex items-center gap-1"
            data-testid="music-upload"
          >
            {uploading ? (
              <Icons.Loader2 size={10} className="animate-spin" />
            ) : (
              <Icons.Upload size={10} />
            )}
            Upload
          </button>
          <input
            ref={fileRef}
            type="file"
            accept="audio/mpeg,audio/mp3,audio/wav,audio/aac"
            hidden
            onChange={upload}
          />
        </div>
        <div className="flex items-center gap-2">
          <select
            value={musicTrackId || ""}
            onChange={(e) => onMusicChange(e.target.value || null)}
            className="flex-1 bg-white/5 border border-white/10 rounded px-2 py-1.5 text-xs text-white"
            data-testid="music-select"
          >
            <option value="">No music</option>
            {tracks.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
              </option>
            ))}
          </select>
          {musicTrackId && (
            <>
              <button
                onClick={() => preview(musicTrackId)}
                className="p-1.5 text-zinc-400 hover:text-white border border-white/10 rounded"
                data-testid="music-preview"
              >
                <Icons.Play size={11} />
              </button>
              <button
                onClick={() => remove(musicTrackId)}
                className="p-1.5 text-zinc-400 hover:text-red-400 border border-white/10 rounded"
              >
                <Icons.Trash2 size={11} />
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
